import { Injectable } from '@angular/core';
import { DataLayerCategories } from '@enums/data-layer-categories';
import { StorageKeys } from '@enums/storage-keys';
import { InHomeBookingResult } from '@Medology/ng-findalab';
import { AnalyteCareCouponDto } from '@models/analyte-care-coupon-dto';
import { BusinessAccountOrder } from '@models/business-account-order';
import { Center } from '@models/center';
import { Coupon } from '@models/coupon';
import { ExtraCharge } from '@models/extra-charge';
import { Fee } from '@models/fee';
import { OrderResponse } from '@models/order-response';
import { OrderUpsell } from '@models/order-upsell';
import { Test } from '@models/test';
import { AbstractStorageService } from '@services/abstract-storage.service';
import { ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StorageService extends AbstractStorageService {
  public testsSubject = new ReplaySubject<Test[]>();

  protected pscKeys = [
    'id',
    'title',
    'country',
    'address',
    'address2',
    'city',
    'state',
    'zip_code',
    'network',
    'phone',
  ];

  public get tests(): Test[] {
    return this.get(StorageKeys.Tests) ?? [];
  }

  public get orderedTests(): Test[] {
    return this.get(StorageKeys.OrderedTests) ?? [];
  }

  public get coupon(): Coupon | null {
    return this.get(StorageKeys.Coupon);
  }

  public get discount(): AnalyteCareCouponDto | null {
    return this.get(StorageKeys.Discount);
  }

  public get fee(): Fee | null {
    return this.get(StorageKeys.Fee);
  }

  public get center(): Center | null {
    return this.get(StorageKeys.Center);
  }

  public get email(): string | null {
    return this.get(StorageKeys.Email);
  }

  public get authToken(): string | null {
    return this.get(StorageKeys.AuthToken);
  }

  public get ecommerceTrackingCategories(): DataLayerCategories[] {
    return this.get(StorageKeys.EcommerceTrackingCategories) ?? [];
  }

  public get baOrder(): BusinessAccountOrder | null {
    return this.get(StorageKeys.BaOrder, false);
  }

  public get order(): OrderResponse {
    return this.get(StorageKeys.Order, false) ?? '';
  }

  public get secondaryPaymentTransactionId(): string | null {
    return this.get(StorageKeys.SecondaryPaymentTransactionId, false);
  }

  public get betterLab(): boolean {
    return !!this.get(StorageKeys.BetterLab);
  }

  public get free(): boolean | null {
    return this.get(StorageKeys.Free);
  }

  public get labChanged(): string | null {
    return this.get(StorageKeys.LabChanged);
  }

  public get paymentType(): string | null {
    return this.get(StorageKeys.PaymentType);
  }

  public get hasPartner(): boolean | null {
    return this.get(StorageKeys.HasPartner);
  }

  /**
   * Get inHomeCollection from localstorage
   */
  public get inHomeCollection(): boolean | null {
    return this.get(StorageKeys.InHomeCollection);
  }

  /**
   * Gets whether the in-home collection service has been paid for from local storage.
   */
  public get isInHomeCollectionPaid(): boolean {
    return !!this.get(StorageKeys.IsIhcPaid);
  }

  /**
   * Gets if the order address page has already been completed from local storage.
   */
  public get addressComplete(): boolean {
    return !!this.get(StorageKeys.AddressComplete);
  }

  public get orderType(): 'email' | 'phone' | 'none' | null {
    return this.get(StorageKeys.OrderType, false);
  }

  /**
   * Retrieve flag used to identify if the user allowed us to message them
   */
  public get sendSMS(): boolean {
    return !!this.get(StorageKeys.SendSms);
  }

  /**
   * Retrieve flag used to determine if patient has informed their email
   */
  public get hasEmail(): boolean {
    return !!this.get(StorageKeys.HasEmail);
  }

  /**
   * Retrieve order total information from local storage
   */
  public get orderTotal(): number | null {
    return this.get(StorageKeys.OrderTotal);
  }

  /**
   * Get the zipcode
   */
  public get zipcode(): string | null {
    return this.get(StorageKeys.ZipCode);
  }

  /**
   * Get in-home booking result
   */
  public get inHomeBookingResult(): InHomeBookingResult | null {
    return this.get(StorageKeys.InHomeBookingResult);
  }

  /**
   * Determines if in home collection is selected.
   */
  public get hasInHomeCollection(): boolean {
    return !!this.inHomeBookingResult;
  }

  /**
   * Checks if the order is shipped.
   */
  public get isShipped(): boolean {
    return this.get(StorageKeys.IsShipped) ?? false;
  }

  /**
   * Get the provider ids for in-home collection.
   */
  public get providerIds(): number[] | null {
    return this.get(StorageKeys.ProviderIds);
  }

  /**
   * Returns Manage User Id from local storage
   */
  public get userId(): number | null {
    const userId = this.get(StorageKeys.UserId, false); // this.get already converts to an integer by calling JSON.parse

    return typeof userId === 'number' && Number.isInteger(userId) ? userId : null;
  }

  /**
   * Gets the extra charges from the local storage.
   */
  public get extraCharges(): ExtraCharge[] {
    return this.get(StorageKeys.ExtraCharges) ?? [];
  }

  /**
   * Gets the order upsells from the local storage.
   */
  public get orderUpsells(): OrderUpsell[] {
    return this.get(StorageKeys.OrderUpsells) ?? [];
  }

  /**
   * Gets the reorder id from local storage
   */
  public get reorderId(): string | null {
    return this.get(StorageKeys.ReorderId, false);
  }

  public set tests(tests: Test[]) {
    this.set(StorageKeys.Tests, tests);
    this.testsSubject.next(tests);
  }

  public set orderedTests(orderedTests: Test[]) {
    this.set(StorageKeys.OrderedTests, orderedTests);
  }

  public set coupon(coupon: Coupon) {
    this.set(StorageKeys.Coupon, coupon);
  }

  public set discount(discount: AnalyteCareCouponDto) {
    this.set(StorageKeys.Discount, discount);
  }

  public set fee(fee: Fee) {
    this.set(StorageKeys.Fee, fee);
  }

  public set center(center: Center) {
    const refinedCenter = {};
    for (const key in center) {
      if (this.pscKeys.includes(key)) {
        refinedCenter[key] = center[key];
      }
    }
    this.set(StorageKeys.Center, center ? refinedCenter : null);
  }

  public set email(email: string) {
    this.set(StorageKeys.Email, email);
  }

  public set authToken(authToken: string) {
    this.set(StorageKeys.AuthToken, JSON.stringify(authToken));
  }

  public set ecommerceTrackingCategories(ecommerceDataLayerCategories: DataLayerCategories[]) {
    this.set(StorageKeys.EcommerceTrackingCategories, ecommerceDataLayerCategories);
  }

  public set baOrder(baOrder: BusinessAccountOrder) {
    this.set(StorageKeys.BaOrder, baOrder, false);
  }

  public set order(order: OrderResponse) {
    this.set(StorageKeys.Order, order, false);
  }

  public set secondaryPaymentTransactionId(transactionId: string) {
    this.set(StorageKeys.SecondaryPaymentTransactionId, transactionId, false);
  }

  public set betterLab(betterLab: boolean) {
    this.set(StorageKeys.BetterLab, betterLab);
  }

  public set labChanged(labChanged: string) {
    this.set(StorageKeys.LabChanged, labChanged);
  }

  public set free(free: boolean) {
    this.set(StorageKeys.Free, free);
  }

  public set paymentType(paymentType: string) {
    this.set(StorageKeys.PaymentType, JSON.stringify(paymentType));
  }

  public set hasPartner(hasPartner: boolean) {
    this.set(StorageKeys.HasPartner, hasPartner);
  }

  /**
   * Set inHomeCollection to localstorage
   */
  public set inHomeCollection(enabled: boolean) {
    this.set(StorageKeys.InHomeCollection, enabled);
  }

  /**
   * Sets whether the in-home collection service has been paid for in local storage.
   */
  public set isInHomeCollectionPaid(isPaid: boolean) {
    this.set(StorageKeys.IsIhcPaid, isPaid);
  }

  /**
   * Set the status of the order address page in local storage.
   *
   * @param {boolean} addressComplete
   */
  public set addressComplete(addressComplete: boolean) {
    this.set(StorageKeys.AddressComplete, addressComplete, true);
  }

  public set orderType(orderType: 'email' | 'phone' | 'none') {
    this.set(StorageKeys.OrderType, orderType, false);
  }

  /**
   * Saves sendSMS flag to local storage
   *
   * @param {boolean} sendSMS User allowed sms messages
   */
  public set sendSMS(sendSMS: boolean) {
    this.set(StorageKeys.SendSms, sendSMS);
  }

  /**
   * Saves flag used to determine if patient has informed their email
   *
   * @param {boolean} hasEmail Patient informed their email
   */
  public set hasEmail(hasEmail: boolean) {
    this.set(StorageKeys.HasEmail, hasEmail);
  }

  /**
   * Saves the order total in local storage
   *
   * @param {number} total Order total
   */
  public set orderTotal(total: number) {
    this.set(StorageKeys.OrderTotal, total);
  }

  /**
   * Saves the zipcode
   *
   * @param {string} zipcode the zipcode
   */
  public set zipcode(zipcode: string) {
    this.set(StorageKeys.ZipCode, JSON.stringify(zipcode));
  }

  /**
   * Save in-home booking result
   * @param inHomeBookingResult
   */
  public set inHomeBookingResult(inHomeBookingResult: InHomeBookingResult) {
    this.set(StorageKeys.InHomeBookingResult, inHomeBookingResult);
  }

  /**
   * Sets the order as shipped or not.
   */
  public set isShipped(isShipped: boolean) {
    this.set(StorageKeys.IsShipped, isShipped);
  }

  /**
   * Set the provider ids for in-home collection.
   */
  public set providerIds(providerIds: number[]) {
    this.set(StorageKeys.ProviderIds, providerIds);
  }

  /**
   * Stores the extra charges in the local storage.
   *
   * @param {ExtraCharge[]} extraCharges the extra charges to store
   */
  public set extraCharges(extraCharges: ExtraCharge[]) {
    this.set(StorageKeys.ExtraCharges, extraCharges);
  }

  /**
   * Stores the order upsells in the local storage.
   *
   * @param {OrderUpsell[]} upsells the order upsells to store
   */
  public set orderUpsells(upsells: OrderUpsell[]) {
    this.set(StorageKeys.OrderUpsells, upsells);
  }

  /**
   * Stores the reorder id in the local storage
   *
   * @param {string} reorderId the reorder id to store
   */
  public set reorderId(reorderId: string) {
    this.set(StorageKeys.ReorderId, reorderId, false);
  }

  /**
   * Get cookie by key
   *
   * @param {string} key The key index to identify the cookie
   */
  public getCookie(key: string): any {
    const cookie = document.cookie.match(new RegExp('(^| )' + key + '=([^;]+)'));

    return cookie ? cookie[2] : null;
  }

  /** {@inheritDoc AbstractStorageService.remove} */
  public override remove(key: string, usePrefix: boolean = true): void {
    localStorage.removeItem(this.getFullKey(key, usePrefix));
  }

  /** {@inheritDoc AbstractStorageService.get} */
  protected override get(key: string, usePrefix: boolean = true): any {
    return this.jsonParseValue(localStorage.getItem(this.getFullKey(key, usePrefix)));
  }

  /** {@inheritDoc AbstractStorageService.set} */
  protected override set(key: string, value: any, usePrefix: boolean = true): void {
    localStorage.setItem(this.getFullKey(key, usePrefix), this.stringifyValue(value));
  }
}
